import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AxiosRequestConfig } from 'axios';
import useAxiosPrivate from './useAxiosPrivate';
import { DASHBOARD_MESSAGES, DASHBOARD_OFFERS, DASHBOARD_SAMPLEREQUESTS } from '../constants/queryKeys';
import { DashboardMessageDto, Offer, SampleRequestResource } from '../api/_generated_';

export default function useDashboard() {
  const axios = useAxiosPrivate();
  const i18n = useTranslation();
  const queryClient = useQueryClient();

  const requestConfig: AxiosRequestConfig = {
    params: {
      lang: i18n.i18n.language,
    },
  };

  const { mutate: markAsRead } = useMutation({
    mutationFn: async (item: DashboardMessageDto) => {
      if (item.entityType === 'offer') {
        await axios.post(`/offers/${item.entityId}/messages/mark-as-read`);
        return;
      }
      if (item.entityType === 'product') {
        await axios.patch(`/product-messages/${item.entityId}/mark-as-read`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_MESSAGES] });
    },
  });

  const { mutate: archive } = useMutation({
    mutationFn: async (item: DashboardMessageDto) => {
      if (item.entityType === 'offer') {
        await axios.post(`/offers/${item.entityId}/messages/archive`);
        return;
      }
      if (item.entityType === 'product') {
        await axios.patch(`/product-messages/${item.entityId}/archive`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_MESSAGES] });
    },
  });

  const queries = useQueries({
    queries: [
      {
        queryKey: [DASHBOARD_MESSAGES],
        queryFn: async () => {
          const res = await axios.get<DashboardMessageDto[]>('/dashboard/messages', requestConfig);
          return res.data;
        },
      },
      {
        queryKey: [DASHBOARD_SAMPLEREQUESTS],
        queryFn: async () => {
          const res = await axios.get<SampleRequestResource[]>('/dashboard/sample-requests', requestConfig);
          return res.data;
        },
      },
      {
        queryKey: [DASHBOARD_OFFERS],
        queryFn: async () => {
          const res = await axios.get<Offer[]>('/dashboard/pending-offers');
          return res.data;
        },
      },
    ],
  });

  return {
    messages: queries[0],
    sampleRequests: queries[1],
    offers: queries[2],
    markAsRead,
    archive,
  };
}
